import { useEffect, useMemo, useState } from "react";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import { catalogFetch } from "./catalogApi.js";

function OrdersTable({
  orders,
  authToken = "",
  onTakeOpposite,
  onCancelOrder,
  disabled,
  emptyMessage = "No orders yet",
  actionMessage = "Take opposite",
}) {
  const [marketTitlesById, setMarketTitlesById] = useState({});

  useEffect(() => {
    let cancelled = false;

    const loadMarkets = async () => {
      if (!authToken) {
        setMarketTitlesById({});
        return;
      }

      try {
        const body = await catalogFetch("/markets", authToken);

        const items = Array.isArray(body.items) ? body.items : [];
        const nextTitles = {};
        for (const item of items) {
          if (!item?.market_id) {
            continue;
          }
          nextTitles[item.market_id] = item.title || item.market_id;
        }

        if (!cancelled) {
          setMarketTitlesById(nextTitles);
        }
      } catch (_err) {
        if (!cancelled) {
          setMarketTitlesById({});
        }
      }
    };

    loadMarkets();
    return () => {
      cancelled = true;
    };
  }, [authToken]);

  const resolvedOrders = useMemo(
    () =>
      orders.map((order) => ({
        ...order,
        market_title: marketTitlesById[order.market_id] || order.market_id,
      })),
    [orders, marketTitlesById]
  );

  const canTakeOpposite = (order) =>
    typeof onTakeOpposite === "function"
    && Number(order.remaining_qty) > 0
    && order.status !== "FILLED";

  const canCancelOrder = (order) =>
    typeof onCancelOrder === "function"
    && Number(order.remaining_qty) > 0
    && String(order.status || "").toUpperCase() === "OPEN";

  return (
    <div className="workspace-table-shell">
      <div className="workspace-table-header">
        <div className="workspace-table-title">My Orders</div>
        <div className="workspace-table-subtitle">Orders you have placed in this workspace.</div>
      </div>
      <div className="table-scroll-region">
      <Table striped bordered hover responsive variant="dark" className="workspace-table">
        <thead>
          <tr>
            <th>Order ID</th>
            <th>Outcome</th>
            <th>Market</th>
            <th>Price</th>
            <th>Quantity</th>
            <th>Remaining</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {resolvedOrders.length === 0 ? (
            <tr className="workspace-table-empty-row">
              <td colSpan={8}>{emptyMessage}</td>
            </tr>
          ) : (
            resolvedOrders.map((order) => (
              <tr key={order.order_id}>
                <td>{order.order_id}</td>
                <td>{order.outcome}</td>
                <td>{order.market_title}</td>
                <td>{order.limit_price}</td>
                <td>{order.quantity}</td>
                <td>{order.remaining_qty}</td>
                <td>{order.status}</td>
                <td>
                  {canCancelOrder(order) ? (
                    <Button
                      size="sm"
                      className="workspace-table-action"
                      variant="outline-danger"
                      disabled={disabled}
                      onClick={() => onCancelOrder(order)}
                    >
                      Cancel Order
                    </Button>
                  ) : canTakeOpposite(order) ? (
                    <Button
                      size="sm"
                      className="workspace-table-action"
                      variant="outline-primary"
                      disabled={disabled}
                      onClick={() => onTakeOpposite(order)}
                    >
                      {actionMessage}
                    </Button>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
      </div>
    </div>
  );
}

export default OrdersTable;
